// src/pages/Settings/Settings.js
import React, { useState, useEffect } from 'react';
import { createCustomField, getCustomFields } from '../../services/settingsService';
import { getTenantUsers, addTenantUser, deleteTenantUser, getCurrentUser } from '../../services/userService';
import './Settings.css';

const Settings = () => {
    // User management state
    const [users, setUsers] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
    const [newUser, setNewUser] = useState({
        username: '',
        email: '',
        password: '',
        role: 'USER'
    });
    const [error, setError] = useState(null);

    // Form fields (Company & Product Information)
    const [companyName, setCompanyName] = useState('');
    const [industryType, setIndustryType] = useState('');
    const [companySize, setCompanySize] = useState('');
    const [companyWebsite, setCompanyWebsite] = useState('');
    const [missionValue, setMissionValue] = useState('');
    const [competitiveAdvantage, setCompetitiveAdvantage] = useState('');
    const [productName, setProductName] = useState('');
    const [productDescription, setProductDescription] = useState('');
    const [pricingModel, setPricingModel] = useState('');
    const [idealCustomerProfile, setIdealCustomerProfile] = useState('');
    const [painPoints, setPainPoints] = useState('');

    // Custom fields state
    const [customFieldName, setCustomFieldName] = useState('');
    const [customFieldType, setCustomFieldType] = useState('text');
    const [customFields, setCustomFields] = useState([]);
    const [customMessage, setCustomMessage] = useState('');

    useEffect(() => {
        const user = getCurrentUser();
        setCurrentUser(user);

        if (user && user.role !== 'SUPER_ADMIN') {
            fetchTenantUsers();
        }
        fetchCustomFields();
    }, []);

    const fetchCustomFields = async () => {
        try {
            const response = await getCustomFields();
            setCustomFields(response.data.customFields);
        } catch (error) {
            console.error('Error fetching custom fields:', error);
        }
    };

    const fetchTenantUsers = async () => {
        try {
            const response = await getTenantUsers();
            setUsers(response.data.users);
        } catch (error) {
            console.error('Error fetching users:', error);
            setError('Failed to fetch users');
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        try {
            await addTenantUser(newUser);
            setIsAddUserModalOpen(false);
            setNewUser({ username: '', email: '', password: '', role: 'USER' });
            fetchTenantUsers();
        } catch (error) {
            console.error('Error adding user:', error);
            setError(error.response?.data?.error || 'Failed to add user');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            try {
                await deleteTenantUser(userId);
                fetchTenantUsers();
            } catch (error) {
                console.error('Error deleting user:', error);
                setError(error.response?.data?.error || 'Failed to delete user');
            }
        }
    };

    // Handler to create a new custom field
    const handleCreateCustomField = async () => {
        if (!customFieldName.trim() || !customFieldType.trim()) {
            setCustomMessage("Custom field name and type are required.");
            return;
        }
        try {
            const response = await createCustomField({ fieldName: customFieldName, fieldType: customFieldType });
            setCustomMessage(response.data.message);
            setCustomFieldName('');
            setCustomFieldType('text');
            fetchCustomFields();
        } catch (error) {
            console.error("Error creating custom field:", error);
            setCustomMessage("Error creating custom field.");
        }
    };

    // Example submit handler for other settings
    const handleSaveUpdate = () => {
        // In real usage, you'd send these fields to your backend.
        alert('Settings saved/updated!');
    };

    // Render user management section only for tenant admins
    const renderUserManagementSection = () => {
        if (currentUser?.role === 'SUPER_ADMIN') {
            return null; // Don't show user management for super admin
        }

        return (
            <div className="user-table-container">
                <div className="section-header">
                    <h2>User Management</h2>
                    <button className="add-user-btn" onClick={() => setIsAddUserModalOpen(true)}>
                        Add New User
                    </button>
                </div>

                {error && <div className="error-message">{error}</div>}

                <table className="user-table">
                    <thead>
                    <tr>
                        <th>Username</th>
                        <th>Email Address</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {users.map((user) => (
                        <tr key={user.id}>
                            <td>{user.username}</td>
                            <td>
                                <a href={`mailto:${user.email}`} className="email-link">
                                    {user.email}
                                </a>
                            </td>
                            <td>{user.role}</td>
                            <td>
                                    <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                                        {user.isActive ? 'Active' : 'Inactive'}
                                    </span>
                            </td>
                            <td>
                                <button
                                    className="delete-btn"
                                    onClick={() => handleDeleteUser(user.id)}
                                >
                                    Delete
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="settings-page">
            <h1>Settings</h1>
            <p className="settings-description">
                Manage user roles and access levels within SLAI. Control who can modify settings,
                access campaign data, and oversee AI performance.
            </p>

            {/* User Management Section */}
            {renderUserManagementSection()}

            {/* Company & Product Information Section */}
            <div className="settings-section">
                <h2>Company & Product Information</h2>
                <p className="section-help-text">
                    Help SLAI understand your business to optimize AI-driven sales conversations.
                </p>
                <div className="form-row">
                    <label>Company Name</label>
                    <input
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                    />
                </div>
                <div className="form-row">
                    <label>Industry Type</label>
                    <select
                        value={industryType}
                        onChange={(e) => setIndustryType(e.target.value)}
                    >
                        <option value="">Select Industry</option>
                        <option value="Software">Software</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Manufacturing">Manufacturing</option>
                    </select>
                </div>
                {/* ... rest of your company information fields ... */}
            </div>

            {/* Custom Fields Section */}
            <div className="settings-section">
                <h3>Custom Fields for Leads</h3>
                <p className="section-help-text">
                    Create custom fields to extend the lead object. These fields will appear on lead profiles.
                </p>
                <div className="form-row">
                    <label>Field Name</label>
                    <input
                        type="text"
                        value={customFieldName}
                        onChange={(e) => setCustomFieldName(e.target.value)}
                    />
                </div>
                <div className="form-row">
                    <label>Field Type</label>
                    <select
                        value={customFieldType}
                        onChange={(e) => setCustomFieldType(e.target.value)}
                    >
                        <option value="text">Text</option>
                        <option value="number">Number</option>
                        <option value="date">Date</option>
                    </select>
                </div>
                <button className="purple-btn" onClick={handleCreateCustomField}>
                    Create Custom Field
                </button>
                {customMessage && (
                    <div style={{ marginTop: '10px', padding: '10px', background: '#f0f0f0', borderRadius: '4px' }}>
                        {customMessage}
                    </div>
                )}
                <div style={{ marginTop: '20px' }}>
                    <h4>Existing Custom Fields</h4>
                    {customFields.length === 0 ? (
                        <p>No custom fields created.</p>
                    ) : (
                        <ul>
                            {customFields.map(field => (
                                <li key={field.id}>
                                    {field.fieldName} ({field.fieldType})
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Add User Modal */}
            {isAddUserModalOpen && (
                <div className="modal">
                    <div className="modal-content">
                        <h2>Add New User</h2>
                        <form onSubmit={handleAddUser}>
                            <div className="form-group">
                                <label>Username</label>
                                <input
                                    type="text"
                                    value={newUser.username}
                                    onChange={(e) => setNewUser({
                                        ...newUser,
                                        username: e.target.value
                                    })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Email</label>
                                <input
                                    type="email"
                                    value={newUser.email}
                                    onChange={(e) => setNewUser({
                                        ...newUser,
                                        email: e.target.value
                                    })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Password</label>
                                <input
                                    type="password"
                                    value={newUser.password}
                                    onChange={(e) => setNewUser({
                                        ...newUser,
                                        password: e.target.value
                                    })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Role</label>
                                <select
                                    value={newUser.role}
                                    onChange={(e) => setNewUser({
                                        ...newUser,
                                        role: e.target.value
                                    })}
                                >
                                    <option value="USER">User</option>
                                    <option value="TENANT_ADMIN">Tenant Admin</option>
                                </select>
                            </div>
                            <div className="modal-actions">
                                <button type="button" onClick={() => setIsAddUserModalOpen(false)}>
                                    Cancel
                                </button>
                                <button type="submit">Add User</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="submit-section">
                <button className="save-update-btn" onClick={handleSaveUpdate}>
                    Save & Update SLAI
                </button>
            </div>
        </div>
    );
};

export default Settings;